import { supabase, supabaseUrl } from '../lib/supabase'
import { getAdminSelectedSchool } from '../utils/adminSchoolContext'
import { type ChatPromptWithVideo } from './chatPrompts'
import type { Video } from './videos'
import { ConversationService } from './conversations'
import { retryWithBackoff, createRetryableErrorFromResponse, RetryableError } from '../utils/retryWithBackoff'

interface ChatRequest {
  message: string
  schoolId?: string // Admin-selected school (header); honored server-side only for admins
  previousResponseId?: string // For Responses API conversation continuity
  // The edge function reads the prompt text, its video context and its model from the
  // database using these ids. Nothing about the prompt is sent from here, so a tampered
  // payload cannot rewrite the AI's instructions or bypass the school's 回答方針.
  chatPromptId?: string // chat_prompts.id — みらいワーク / video chat
  homeworkId?: string   // homeworks.id — 宿題 chat
  // Lets the edge function record the instructions it composed against this chat, so
  // 管理画面 → チャット監視 can show what was actually sent long after the edge logs
  // (24h retention) are gone. Absent for the 宿題 chat, which keeps no conversation.
  conversationId?: string
  conversationHistory?: Array<{
    sender: 'user' | 'ai'
    message: string
  }>
}

interface EdgeFunctionResponse {
  success: boolean
  message?: string
  error?: string
  timestamp: string
  responseId?: string // For Responses API conversation continuity
  usage?: {
    prompt_tokens: number
    completion_tokens: number
    total_tokens: number
  }
  model?: string
}

interface ChatContext {
  video?: Video
  chatPrompt?: ChatPromptWithVideo
  homeworkId?: string // Set by the homework chat; takes precedence over chatPrompt
  conversationHistory?: Array<{
    sender: 'user' | 'ai'
    message: string
  }>
  conversationId?: string
  signal?: AbortSignal
}

// The openai-chat edge function reports every failure it handles as JSON carrying an
// `error` string. Returns that message, or null when the body is not the function's own
// (an opaque gateway/platform failure, which is the only kind worth retrying).
function parseEdgeFunctionError(body: string): string | null {
  try {
    const parsed = JSON.parse(body) as { error?: unknown }
    if (typeof parsed?.error === 'string' && parsed.error.trim()) {
      return parsed.error
    }
  } catch {
    // Not JSON, so not a response the handler produced
  }
  return null
}

export class OpenAIService {
  private static getEdgeFunctionUrl(): string {
    if (!supabaseUrl) {
      throw new Error('Supabase URL is not configured. Please set VITE_SUPABASE_URL in your environment.')
    }
    return `${supabaseUrl}/functions/v1/openai-chat`
  }

  private static async getAuthHeaders() {
    const { data: { session } } = await supabase.auth.getSession()
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${session?.access_token || ''}`
    }
  }

  static async generateResponse(
    userMessage: string, 
    context: ChatContext = {}
  ): Promise<string> {
    const startTime = Date.now()
    try {
      const headers = await this.getAuthHeaders()
      const url = this.getEdgeFunctionUrl()

      // Build request payload
      const requestPayload: ChatRequest = {
        message: userMessage,
        conversationHistory: context.conversationHistory
      }

      // Admins choose the active school in the header; pass it so school AI policies
      // apply. Empty for non-admins (and ignored server-side for them anyway).
      const adminSchoolId = getAdminSelectedSchool()?.id
      if (adminSchoolId) {
        requestPayload.schoolId = adminSchoolId
      }

      // Get latest response ID for conversation continuity
      if (context.conversationId) {
        requestPayload.conversationId = context.conversationId
        try {
          const latestResponseId = await ConversationService.getLatestResponseId(context.conversationId)
          if (latestResponseId) {
            requestPayload.previousResponseId = latestResponseId
            console.log('=== Using Previous Response ID for Continuity ===')
            console.log('Conversation ID:', context.conversationId)
            console.log('Previous Response ID:', latestResponseId)
            console.log('================================================')
          }
        } catch (err) {
          console.warn('Could not fetch latest response ID:', err)
        }
      }

      // Identify what is being chatted about; the edge function looks the rest up. A
      // homework id is sent on its own field because HomeworkChat's prompt object
      // carries a homeworks.id, which the server must not mistake for a chat_prompts.id.
      if (context.homeworkId) {
        requestPayload.homeworkId = context.homeworkId
      } else if (context.chatPrompt) {
        requestPayload.chatPromptId = context.chatPrompt.id
      }

      const data: EdgeFunctionResponse = await retryWithBackoff(async () => {
        const response = await fetch(url, {
          method: 'POST',
          headers,
          body: JSON.stringify(requestPayload),
          signal: context.signal
        })

        if (!response.ok) {
          const errorText = await response.text()
          console.error('Edge Function Error:', errorText)

          if (response.status === 401) {
            throw new Error('認証が必要です。ログインしてください。')
          }

          // A structured error body means the edge function's handler ran and
          // already exhausted its own retry budget around the OpenAI call, so this
          // is its final answer rather than a transient blip. Retrying here would
          // only multiply the load on a verdict that comes back identical -- a bad
          // request such as context_length_exceeded never resolves itself. Retries
          // stay reserved for opaque platform failures, which carry no such body.
          const handlerError = parseEdgeFunctionError(errorText)
          if (handlerError) {
            throw new RetryableError(handlerError, false)
          }

          if (response.status === 429) {
            throw createRetryableErrorFromResponse(response, 'API利用制限に達しました。しばらく時間を置いてから再試行してください。')
          } else if (response.status === 500) {
            throw createRetryableErrorFromResponse(response, 'サーバーでエラーが発生しました。後でもう一度お試しください。')
          } else {
            throw new Error(`AI応答の生成に失敗しました (${response.status})`)
          }
        }

        return response.json()
      }, {
        maxRetries: 3,
        baseDelay: 1000,
        maxDelay: 16000
      })
      
      // Debug: Log response from edge function
      console.log('=== OpenAI Service Response Debug ===')
      console.log('Response:', JSON.stringify(data, null, 2))
      
      if (!data.success || !data.message) {
        throw new Error(data.error || 'AIから有効な応答が得られませんでした')
      }

      // Store response metadata for later use
      const responseTime = Date.now() - startTime
      this.lastResponseMetadata = {
        usage: data.usage,
        model: data.model || context.chatPrompt?.model || 'gpt-4',
        responseTimeMs: responseTime,
        responseId: data.responseId // Store for potential conversation continuity
      }
      
      console.log('Response time:', responseTime, 'ms')
      console.log('=====================================')

      return data.message
    } catch (error) {
      // Don't log or transform abort errors - let them propagate
      if (error instanceof Error && error.name === 'AbortError') {
        throw error
      }

      if (error instanceof Error) {
        console.error('OpenAI Service Error:', error.message)
        throw error
      } else {
        console.error('Unknown error in OpenAI Service:', error)
        throw new Error('AI応答の生成中に予期しないエラーが発生しました')
      }
    }
  }

  private static lastResponseMetadata: {
    usage?: {
      prompt_tokens: number
      completion_tokens: number
      total_tokens: number
    }
    model: string
    responseTimeMs: number
    responseId?: string // For Responses API conversation continuity
  } | null = null

  static getLastResponseMetadata() {
    return this.lastResponseMetadata
  }

  static async generateContextualResponse(
    userMessage: string,
    video: Video,
    conversationHistory: Array<{sender: 'user' | 'ai', message: string}> = [],
    chatPrompt?: ChatPromptWithVideo
  ): Promise<string> {
    const context: ChatContext = {
      video,
      chatPrompt,
      conversationHistory
    }

    return this.generateResponse(userMessage, context)
  }

  static isConfigured(): boolean {
    try {
      this.getEdgeFunctionUrl()
      return true
    } catch (error) {
      console.error('OpenAI Service configuration error:', error)
      return false
    }
  }
}

export type { ChatContext, ChatRequest, EdgeFunctionResponse }